"use client";

import { InterestGroup } from "../../../app/storyblok/[localeString]/support-hub/types/contact.types";
import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { useCrhClickCountingContext } from "../../../helpers/CrhClickCountingContext";
import {
  CATEGORY_QUERY_STRING_KEY,
  TOPIC_QUERY_STRING_KEY,
} from "../../../helpers/contactSettings";
import { useParams, useSearchParams } from "next/navigation";
import { useMemo } from "react";

export function useSideMenuLogic({
  interestGroups,
}: {
  interestGroups: InterestGroup[];
}) {
  const searchParams = useSearchParams();
  const contactPageRouter = useContactPageRouterContext();
  const { trackClick } = useCrhClickCountingContext();

  const params = useParams() ?? {};
  const interestGroupSlug =
    typeof params.interestGroupSlug === "string"
      ? params.interestGroupSlug
      : Array.isArray(params.interestGroupSlug)
        ? params.interestGroupSlug[0]
        : undefined;

  const selectedInterestGroup = interestGroupSlug
    ? interestGroups.find((item) => item.slug === interestGroupSlug)
    : undefined;

  // Filter categories to only show those that belong to the selected interest group
  const availableCategories = useMemo(
    () => selectedInterestGroup?.categories ?? [],
    [selectedInterestGroup?.categories],
  );

  const selectedCategoriesIds =
    searchParams?.getAll(CATEGORY_QUERY_STRING_KEY) ?? [];
  const selectedCategories = availableCategories?.filter((category) =>
    selectedCategoriesIds.includes(category.uuid),
  );

  // Filter topics based on selection context:
  // If categories selected → show topics from selected categories only
  // If no categories selected → show all topics from available categories (for count display)
  const availableTopics =
    selectedCategories.length > 0
      ? Array.from(
          new Map(
            selectedCategories
              .flatMap((category) => category.topics || [])
              .map((topic) => [topic.uuid, topic]),
          ).values(),
        )
      : Array.from(
          new Map(
            availableCategories
              .flatMap((category) => category.topics || [])
              .map((topic) => [topic.uuid, topic]),
          ).values(),
        );

  // Topics panel is disabled when no categories are selected or no topics available
  const isTopicsDisabled =
    selectedCategories.length === 0 || availableTopics.length === 0;

  const selectedTopicsIds = useMemo(
    () => searchParams?.getAll(TOPIC_QUERY_STRING_KEY) ?? [],
    [searchParams],
  );

  // Calculate total results count based on selected categories and topics
  const totalResultsCount = useMemo(() => {
    // Get all questions from selected categories, or all categories in the interest group if none selected
    const allQuestions =
      selectedCategories.length > 0
        ? selectedCategories.flatMap((category) => category.questions || [])
        : selectedInterestGroup
          ? availableCategories.flatMap((category) => category.questions || [])
          : [];

    // If no questions available, return 0
    if (allQuestions.length === 0) {
      return 0;
    }

    // If topics are selected, filter questions by those topics
    if (selectedTopicsIds.length > 0) {
      const filteredQuestions = allQuestions.filter((question) =>
        question.topicUuids.some((topicUuid) =>
          selectedTopicsIds.includes(topicUuid),
        ),
      );
      return filteredQuestions.length;
    }

    // If no topics selected, return all questions
    return allQuestions.length;
  }, [
    selectedCategories,
    selectedTopicsIds,
    selectedInterestGroup,
    availableCategories,
  ]);

  // Detect homepage state - no selections made
  const isHomepage =
    !selectedInterestGroup &&
    selectedCategoriesIds.length === 0 &&
    selectedTopicsIds.length === 0;

  return {
    interestGroupSlug,
    selectedInterestGroup,
    availableCategories,
    selectedCategoriesIds,
    selectedCategories,
    availableTopics,
    isTopicsDisabled,
    selectedTopicsIds,
    totalResultsCount,
    isHomepage,
    contactPageRouter,
    trackClick,
  };
}
